import { diffDocuments } from './core/differ';
import { parseHtml } from './parser/parser';
import { injectColgroups } from './renderer/injectColgroups';
import { renderDiffTree, type RenderDiffTreeOptions } from './renderer/renderDiffTree';
import { createRenderSpecialNode } from './runtime/renderSpecialNode';
import type { TDocDiffRuntime, TDocDiffOptions } from './runtime/types';
import type { TContentHooks } from './content/renderContent';
import type { TDiffResult } from './types';

// ---------------------------------------------------------------------------
// Low-level API (no runtime - caller provides renderSpecialNode directly)
// ---------------------------------------------------------------------------

/**
 * Low-level diff pipeline: parse, diff, render.
 *
 * Use this for direct engine access or testing. For product code,
 * prefer `diffHtmlTree` which wires the runtime automatically.
 */
export const diffAndRenderTree = async (prevHtml: string, currHtml: string, options: RenderDiffTreeOptions = {}) => {
  const optionsWithCache: RenderDiffTreeOptions = {
    ...options,
    mathCache: options.mathCache ?? new Map<string, string>(),
  };

  const prevRoot = parseHtml(prevHtml);
  const currRoot = parseHtml(currHtml);
  const diffResult = diffDocuments(currRoot, prevRoot);

  return {
    html: injectColgroups(await renderDiffTree(diffResult.root, optionsWithCache)),
    diffResult,
  };
};

// ---------------------------------------------------------------------------
// High-level API (runtime-based - for product code)
// ---------------------------------------------------------------------------

export type TDiffHtmlTreeOptions = {
  runtime: TDocDiffRuntime;
  opts: TDocDiffOptions;
  hooks?: TContentHooks;
};

/** Internal options - adds recursion knobs that callers should not set. */
type TDiffHtmlTreeInternalOptions = TDiffHtmlTreeOptions & {
  depth?: number;
  mathCache?: Map<string, string>;
};

/**
 * High-level diff pipeline with runtime-based special node rendering.
 *
 * This is the intended entry point for product code (FE and BE).
 * It wires the `TDocDiffRuntime` into the `renderSpecialNode` callback
 * and handles recursive block diffing via the `recursiveDiff` closure.
 *
 * `hooks` are passed through to `renderContent` for block expansion
 * (platform-specific image/LaTeX post-processing).
 */
export const diffHtmlTree = async (
  prevHtml: string,
  currHtml: string,
  options: TDiffHtmlTreeOptions | TDiffHtmlTreeInternalOptions
): Promise<{ html: string; diffResult: TDiffResult }> => {
  const internalOpts = options as TDiffHtmlTreeInternalOptions;
  const { runtime, opts, hooks } = options;
  const depth = internalOpts.depth ?? 0;
  const mathCache = internalOpts.mathCache ?? new Map<string, string>();

  const recursiveDiff = async (nestedPrevHtml: string, nestedCurrHtml: string): Promise<string> => {
    const result = await diffHtmlTree(nestedPrevHtml, nestedCurrHtml, {
      runtime,
      opts,
      depth: depth + 1,
      hooks,
      mathCache,
    } satisfies TDiffHtmlTreeInternalOptions);
    return result.html;
  };

  const renderSpecialNode = createRenderSpecialNode(runtime, opts, depth, recursiveDiff, hooks);
  return diffAndRenderTree(prevHtml, currHtml, { renderSpecialNode, mathCache });
};

// ---------------------------------------------------------------------------
// Re-exports
// ---------------------------------------------------------------------------

export { parseHtml } from './parser/parser';
export { serializeElement } from './parser/parser';
export { diffDocuments } from './core/differ';
export { escapeHtml } from './renderer/html';
export { formatReferenceHtml } from './runtime/formatReference';
export { injectColgroups } from './renderer/injectColgroups';
export { renderContent } from './content/renderContent';
export { wrapTemplateFieldTags } from './content/wrapTemplateFieldTags';
export type { TContentHooks } from './content/renderContent';
export type { RenderDiffTreeOptions, RenderSpecialNodeHelpers } from './renderer/renderDiffTree';
export type { TDiffNode, TDiffResult, DiffMark, TTextDiffSegment } from './types';
export type {
  TDiffSide,
  TDocDiffRuntime,
  TDocDiffOptions,
  TReferenceData,
  THeadingInfo,
  TBlockInfo,
  TVersionedPair,
} from './runtime/types';
